import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

export interface Court {
  id: number;
  name: string;
}

export interface Booking {
  id: number | string;
  courtId: number;
  date: string;
  startTime: string;
  endTime: string;
  userName: string;
  status: 'pending' | 'confirmed';
  firstName?: string;
  lastName?: string;
  address?: string;
  email?: string;
  photoUrl?: string;
  adminNotes?: string;
}

export interface Settings {
  startTime: string;
  endTime: string;
  slotDuration: string;
  adminGlobalNotes?: string;
  adminGlobalImage?: string;
}

interface BookingContextType {
  courts: Court[];
  bookings: Booking[];
  settings: Settings | null;
  addBooking: (booking: Omit<Booking, 'id'>) => Promise<void>;
  confirmBooking: (id: number | string) => Promise<void>;
  cancelBooking: (id: number | string) => Promise<void>;
  getBookingsForDate: (date: string) => Booking[];
  getBookingsForCourtAndDate: (courtId: number, date: string) => Booking[];
  isSlotOccupied: (courtId: number, date: string, startTime: string, endTime: string) => boolean;
  isSlotPending: (courtId: number, date: string, startTime: string, endTime: string) => boolean;
  currentUser: string;
  setCurrentUser: (name: string) => void;
  refreshBookings: () => Promise<void>;
  refreshConfig: () => Promise<void>;
  loadConfigAndBookings: () => Promise<void>;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [courts, setCourts] = useState<Court[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [currentUser, setCurrentUser] = useState<string>('Guest');

  useEffect(() => {
    const storedUser = localStorage.getItem('bookingUser');
    if (storedUser) setCurrentUser(storedUser);
  }, []);

  useEffect(() => {
    localStorage.setItem('bookingUser', currentUser);
  }, [currentUser]);

  useEffect(() => {
    loadConfigAndBookings();
  }, []);

  const refreshConfig = async () => {
    try {
      const [courtsData, settingsData] = await Promise.all([
        api.getCourts(),
        api.getSettings()
      ]);
      setCourts(courtsData);
      setSettings({
        startTime: settingsData.startTime || '07:00',
        endTime: settingsData.endTime || '21:00',
        slotDuration: settingsData.slotDuration || '60',
        adminGlobalNotes: settingsData.adminGlobalNotes || '',
        adminGlobalImage: settingsData.adminGlobalImage || '',
      });
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };

  const refreshBookings = async () => {
    try {
      const data = await api.getBookings();
      setBookings(data.map((b: any) => ({
        id: b.id,
        courtId: b.court_id,
        date: b.date,
        startTime: b.start_time,
        endTime: b.end_time,
        userName: b.user_name,
        status: b.status,
        firstName: b.first_name || '',
        lastName: b.last_name || '',
        address: b.address || '',
        email: b.email || '',
        photoUrl: b.photo_url || '',
        adminNotes: b.admin_notes || '',
      })));
    } catch (error) {
      console.error('Failed to load bookings:', error);
      setBookings([]);
    }
  };

  const loadConfigAndBookings = async () => {
    await Promise.all([refreshConfig(), refreshBookings()]);
  };

  const addBooking = async (booking: Omit<Booking, 'id'>) => {
    try {
      const result = await api.createBooking({
        court_id: booking.courtId,
        date: booking.date,
        start_time: booking.startTime,
        end_time: booking.endTime,
        user_name: booking.userName,
        status: booking.status || 'pending',
        last_name: booking.lastName || '',
        first_name: booking.firstName || '',
        address: booking.address || '',
        email: booking.email || '',
        photo_url: booking.photoUrl || '',
        admin_notes: booking.adminNotes || '',
      });
      if (result.success) {
        const newBooking: Booking = { ...booking, id: result.id };
        setBookings(prev => [...prev, newBooking]);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
    }
  };

  const confirmBooking = async (id: number | string) => {
    try {
      const result = await api.updateBookingStatus(Number(id), 'confirmed');
      if (result.success) {
        setBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'confirmed' } : b));
      }
    } catch (error) {
      console.error('Error confirming booking:', error);
    }
  };

  const cancelBooking = async (id: number | string) => {
    try {
      const result = await api.deleteBooking(Number(id));
      if (result.success) {
        setBookings(prev => prev.filter(b => b.id !== id));
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
    }
  };

  const getBookingsForDate = (date: string) => {
    return bookings.filter(b => b.date === date);
  };

  const getBookingsForCourtAndDate = (courtId: number, date: string) => {
    return bookings.filter(b => b.courtId === courtId && b.date === date);
  };

  // == Block any slot that is pending OR confirmed ==
  const isSlotOccupied = (courtId: number, date: string, startTime: string, endTime: string) => {
    const slots = getBookingsForCourtAndDate(courtId, date).filter(b => b.status === 'pending' || b.status === 'confirmed');
    return slots.some(b => (startTime < b.endTime && endTime > b.startTime));
  };

  // == Check if a slot is pending (any user) ==
  const isSlotPending = (courtId: number, date: string, startTime: string, endTime: string) => {
    const slots = getBookingsForCourtAndDate(courtId, date).filter(b => b.status === 'pending');
    return slots.some(b => (startTime < b.endTime && endTime > b.startTime));
  };

  return (
    <BookingContext.Provider value={{
      courts,
      bookings,
      settings,
      addBooking,
      confirmBooking,
      cancelBooking,
      getBookingsForDate,
      getBookingsForCourtAndDate,
      isSlotOccupied,
      isSlotPending,
      currentUser,
      setCurrentUser,
      refreshBookings,
      refreshConfig,
      loadConfigAndBookings,
    }}>
      {children}
    </BookingContext.Provider>
  );
};

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) throw new Error('useBooking must be used within a BookingProvider');
  return context;
};